<template>
  <div class="widget-accordion" :class="config?.theme?.components?.accordion">
    <div class="widget-accordion_header--wrapper" @click.prevent="toggleAccordion">
      <div class="widget-accordion_header">
        <WidgetIcon
          :icon="icon || themeIcons.info"
          :class="twMerge(primaryIconVariants({ variant: getClientTheme() }), 'bg-transparent')"
        />
        <h4 class="widget-accordion_title">{{ title }}</h4>
      </div>
      <WidgetIcon
        :icon="downIcon || themeIcons.chevronDown"
        :class="[twMerge(caretIconVariants({ variant: getClientTheme() }), 'bg-transparent'), isOpen && 'rotate-180']"
      />
    </div>
    <HeightTransition :visible="isOpen" inner-tag="ul">
      <li class="widget-accordion_item" v-for="(bullet, index) in bullets" :key="index">
        <div class="grid grid-flow-col items-start justify-start gap-x-3">
          <div class="flex h-full w-full items-center justify-start">
            <div
              :class="
                twMerge('widget-icon widget-accordion_bullet--icon', primaryIconVariants({ variant: getClientTheme() }))
              "
            ></div>
          </div>
          <p class="widget-accordion_bullet">{{ bullet }}</p>
        </div>
      </li>
    </HeightTransition>
  </div>
</template>

<script lang="ts" setup>
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import HeightTransition from 'src/components/base/HeightTransition.vue';
  import { ref } from 'vue';
  import { config } from 'src/utils/config';
  import { cva } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, getClientTheme } from 'src/utils/config';
  import { sendAnalytics } from 'src/utils/analytics';

  export type Props = {
    title: string;
    bullets: string[];
    icon?: string;
    downIcon?: string;
    upIcon?: string;
  };

  const props = defineProps<Props>();

  const primaryIconVariants = cva('text-primary bg-primary flex-shrink-0', {
    variants: {
      variant: {
        default: 'size-6',
        audi: 'size-6',
        volkswagen: 'size-6',
        bentley: 'size-6',
        vwg: 'size-6',
        samsung: 'size-6',
        mercedes: 'size-6',
        bmw: 'size-6 text-fontPrimary bg-fontPrimary',
        toyota: 'size-6',
        jeep: 'size-6 text-secondary bg-secondary',
        mopar: 'size-6',
        mini: 'size-6 text-fontPrimary bg-fontPrimary',
        jaguar: 'size-6',
        jlr: 'size-6 text-fontSecondary bg-fontSecondary',
        common: 'size-6 text-current bg-current',
        referral: 'size-6 text-fontSecondary bg-fontSecondary',
        landrover: 'size-6',
        volvo: 'text-fontPrimary bg-fontPrimary',
        tesla: 'text-fontPrimary bg-fontPrimary',
        aa: 'text-fontPrimary bg-fontPrimary',
        suzuki: 'text-negative bg-negative',
        cinch: 'text-fontPrimary bg-fontPrimary',
        crashdaddy: 'text-fontPrimary bg-fontPrimary',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });
  const caretIconVariants = cva('text-primary bg-primary flex-shrink-0', {
    variants: {
      variant: {
        default: 'size-6',
        audi: 'size-6 text-fontPrimary',
        volkswagen: 'size-6',
        bentley: 'size-6',
        vwg: 'size-6',
        samsung: 'size-6',
        mercedes: 'size-6 text-primary',
        bmw: 'size-6 text-fontPrimary',
        toyota: 'size-6',
        mopar: 'size-6',
        jeep: 'size-6 text-secondary',
        mini: 'size-6 text-fontPrimary',
        jaguar: 'size-6',
        jlr: 'size-6',
        common: 'size-6 text-current',
        referral: 'size-6',
        landrover: '',
        volvo: 'text-fontPrimary bg-fontPrimary',
        tesla: 'text-fontPrimary bg-fontPrimary',
        aa: 'text-fontPrimary bg-fontPrimary',
        suzuki: 'text-fontPrimary bg-fontPrimary',
        cinch: 'text-fontPrimary bg-fontPrimary',
        crashdaddy: 'text-fontPrimary bg-fontPrimary',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });
  const isOpen = ref(false);
  const bullets = ref(props.bullets);
  if (bullets.value?.length) {
    bullets.value = bullets.value.map((bullet) => bullet.replace('#clientname', config.value.name));
  }
  function toggleAccordion() {
    isOpen.value = !isOpen.value;
    sendAnalytics('event', 'accordion', {
      event_category: 'MODE',
      event_label: `Accordion ${isOpen.value ? 'Opened' : 'Closed'}`,
      value: isOpen.value,
    });
  }
</script>
